import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Divider,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  theme as antdTheme,
} from 'antd';
import type { TableProps } from 'antd';
import { space } from '@viq-admin/ui';
import { salesforceResultLine } from './salesforceResult';
import type {
  BatchLead,
  CrossTenantSalesforceApi,
  SalesforceExportMode,
  SalesforceExportResult,
} from './types';

interface Props {
  open: boolean;
  conferenceId: string;
  conferenceName: string;
  salesforceApi: CrossTenantSalesforceApi;
  onClose: () => void;
}

const CONFIRM_WORD = 'CONFIRM';

const MODE_OPTIONS: Array<{
  value: SalesforceExportMode;
  label: string;
  description: string;
}> = [
  { value: 'new', label: 'New only', description: 'Skip leads already pushed for this event' },
  {
    value: 'resync_all',
    label: 'Re-sync all',
    description: 'Push every selected lead again, replacing the Salesforce values this push wrote before',
  },
];

/**
 * Cross-tenant Salesforce export dialog (super-admin console). Adapts the
 * portal's own-company `SalesforceExportDialog`, but because the console has no
 * company of its own the flow is:
 *
 *   1. Pick a TARGET COMPANY (only SF-connected tenants).
 *   2. That company's leads for this conference load with their per-lead SF
 *      sync state (synced / failed / pending); checkbox-select the ones to push.
 *   3. Choose a mode (New only / Re-sync all).
 *   4. Type `CONFIRM` (all caps) — the push button stays disabled until it
 *      matches exactly. This mirrors the destructive-confirm pattern
 *      (`@viq-admin/ui`'s PurgeUserModal) because a cross-tenant write into
 *      another company's Salesforce is privileged.
 *   5. Push → show the `{ synced, skipped, failed }` result.
 *
 * Parameterized by a shell-provided `salesforceApi` (uses antd theme tokens
 * only, so light/dark is automatic).
 */
export function SalesforceExportDialog({
  open,
  conferenceId,
  conferenceName,
  salesforceApi,
  onClose,
}: Props) {
  const { token } = antdTheme.useToken();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [mode, setMode] = useState<SalesforceExportMode>('new');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SalesforceExportResult | null>(null);

  // Reset everything each time the dialog re-opens.
  useEffect(() => {
    if (open) {
      setCompanyId(null);
      setMode('new');
      setSelectedRowKeys([]);
      setConfirmText('');
      setResult(null);
    }
  }, [open]);

  // Reset the per-company selection + confirm + result whenever the target
  // company changes (a fresh cross-tenant target must be re-confirmed).
  useEffect(() => {
    setSelectedRowKeys([]);
    setConfirmText('');
    setResult(null);
  }, [companyId]);

  const companiesQuery = useQuery({
    queryKey: ['salesforceCompanies'],
    queryFn: () => salesforceApi.listSalesforceCompanies(),
    enabled: open,
  });

  const leadsQuery = useQuery({
    queryKey: ['salesforceCompanyLeads', conferenceId, companyId],
    queryFn: () => salesforceApi.fetchCompanyLeads(conferenceId, companyId!),
    enabled: open && !!companyId,
  });

  const membersQuery = useQuery({
    queryKey: ['salesforceMembers', conferenceId, companyId],
    queryFn: () => salesforceApi.fetchMembers(conferenceId, companyId!),
    enabled: open && !!companyId,
    staleTime: 30_000,
  });

  const sfByPerson = useMemo(() => {
    const m = new Map<string, { syncedAt: string | null; syncError: string | null }>();
    for (const r of membersQuery.data ?? []) {
      m.set(r.personId, { syncedAt: r.syncedAt, syncError: r.syncError });
    }
    return m;
  }, [membersQuery.data]);

  const leads = leadsQuery.data ?? [];
  const personIds = selectedRowKeys as string[];
  const canPush =
    !!companyId && personIds.length > 0 && confirmText === CONFIRM_WORD && !busy;

  async function onPush() {
    if (!companyId) return;
    setBusy(true);
    try {
      const r = await salesforceApi.exportToSalesforce(conferenceId, {
        targetCompanyId: companyId,
        personIds,
        mode,
      });
      setResult(r);
      if (r.failed.length === 0 && !(r.incomplete?.length)) {
        void message.success(`Pushed ${r.synced} ${r.synced === 1 ? 'lead' : 'leads'} to Salesforce.`);
      } else {
        void message.warning(salesforceResultLine(r).message);
      }
      // Selection consumed; require a fresh CONFIRM before any re-push.
      setSelectedRowKeys([]);
      setConfirmText('');
      void membersQuery.refetch();
    } catch (err) {
      void message.error((err as { message?: string }).message ?? 'Couldn’t push to Salesforce.');
    } finally {
      setBusy(false);
    }
  }

  const columns: NonNullable<TableProps<BatchLead>['columns']> = [
    {
      dataIndex: 'preview',
      title: 'Name',
      ellipsis: true,
      render: (v: string) => v || '(no name)',
    },
    {
      key: 'titleCompany',
      title: 'Title · Company',
      width: 200,
      ellipsis: true,
      render: (_: unknown, row: BatchLead) => {
        const t = row.title?.trim();
        const c = row.companyName?.trim();
        if (!t && !c) return <Typography.Text type="secondary">—</Typography.Text>;
        if (t && c) return `${t} · ${c}`;
        return t || c;
      },
    },
    {
      key: 'salesforce',
      title: 'Salesforce',
      width: 130,
      render: (_: unknown, row: BatchLead) => {
        const s = sfByPerson.get(row.id);
        if (s?.syncedAt) return <Tag color="green">In Salesforce</Tag>;
        if (s?.syncError) {
          return (
            <Tooltip title={s.syncError}>
              <Tag color="red">Failed</Tag>
            </Tooltip>
          );
        }
        return <Typography.Text type="secondary">Not pushed yet</Typography.Text>;
      },
    },
  ];

  return (
    <Modal
      open={open}
      title={`Push to Salesforce: ${conferenceName}`}
      onCancel={onClose}
      width={720}
      footer={null}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message="This writes to another company’s Salesforce"
          description="The selected leads go into the target company’s own Salesforce org. Check the company and the leads you picked before you push."
        />

        <div>
          <Typography.Text strong>Target company</Typography.Text>
          <Select
            value={companyId ?? undefined}
            onChange={(v) => setCompanyId(v)}
            placeholder="Choose a company with Salesforce connected"
            loading={companiesQuery.isLoading}
            style={{ width: '100%', marginTop: space.sm }}
            options={(companiesQuery.data ?? []).map((c) => ({
              value: c.companyId,
              label: c.companyName || c.companyId,
            }))}
            notFoundContent={
              companiesQuery.isLoading ? 'Loading…' : 'No companies have Salesforce connected'
            }
          />
        </div>

        {companyId && (
          <>
            <Divider style={{ margin: '4px 0' }} />

            <div>
              <Typography.Text strong>Leads</Typography.Text>
              <Typography.Text type="secondary" style={{ marginLeft: space.sm }}>
                {personIds.length} selected
              </Typography.Text>
              <Table
                rowKey="id"
                size="small"
                style={{ marginTop: space.sm }}
                dataSource={leads}
                loading={leadsQuery.isLoading || membersQuery.isLoading}
                pagination={false}
                scroll={{ y: 260 }}
                columns={columns}
                rowSelection={{
                  selectedRowKeys,
                  onChange: setSelectedRowKeys,
                  preserveSelectedRowKeys: true,
                }}
              />
            </div>

            <div>
              <Typography.Text strong>Mode</Typography.Text>
              <Radio.Group
                value={mode}
                onChange={(e) => setMode(e.target.value as SalesforceExportMode)}
                style={{ display: 'flex', flexDirection: 'column', gap: space.sm, marginTop: space.sm }}
              >
                {MODE_OPTIONS.map((opt) => (
                  <Radio key={opt.value} value={opt.value}>
                    <strong>{opt.label}</strong>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {opt.description}
                    </Typography.Text>
                  </Radio>
                ))}
              </Radio.Group>
            </div>

            <div>
              <Typography.Text strong>
                Type <code style={{ color: token.colorError }}>{CONFIRM_WORD}</code> to
                enable the push
              </Typography.Text>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                style={{ marginTop: space.sm }}
              />
            </div>
          </>
        )}

        {result && (
          <Alert
            type={salesforceResultLine(result).type}
            message={salesforceResultLine(result).message}
            description={
              result.failed.length ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {result.failed.slice(0, 10).map((f) => (
                    <li key={f.personId}>{f.reason}</li>
                  ))}
                </ul>
              ) : undefined
            }
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.sm }}>
          <Button onClick={onClose} disabled={busy}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          <Button type="primary" danger onClick={onPush} loading={busy} disabled={!canPush}>
            Push to Salesforce
          </Button>
        </div>
      </Space>
    </Modal>
  );
}
